import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertCircle, X } from "lucide-react";
import { Button } from "./ui/button";

interface ConfirmModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    variant?: "default" | "destructive";
}

export function ConfirmModal({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    confirmText = "Confirm",
    cancelText = "Cancel",
    variant = "default",
}: ConfirmModalProps) {
    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                    />
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        className="relative w-full max-w-md bg-[#0A0A0B] border border-white/10 rounded-[2rem] shadow-2xl overflow-hidden"
                    >
                        <div className="p-6 pt-8 text-center space-y-4">
                            <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center ${variant === "destructive" ? "bg-red-500/10 text-red-500" : "bg-primary/10 text-primary"
                                }`}>
                                <AlertCircle size={32} />
                            </div>

                            <div className="space-y-2">
                                <h3 className="text-xl font-bold text-white">{title}</h3>
                                <p className="text-white/60 text-sm leading-relaxed">
                                    {message}
                                </p>
                            </div>
                        </div>

                        <div className="p-6 bg-white/5 border-t border-white/5 flex gap-3">
                            <Button
                                variant="ghost"
                                onClick={onClose}
                                className="flex-1 h-12 rounded-xl text-white/60 hover:text-white hover:bg-white/5"
                            >
                                {cancelText}
                            </Button>
                            <Button
                                variant={variant === "destructive" ? "destructive" : "default"}
                                onClick={() => {
                                    onConfirm();
                                    onClose();
                                }}
                                className={`flex-1 h-12 rounded-xl font-bold ${variant === "default" ? "bg-primary hover:bg-primary/90 text-white" : ""
                                    }`}
                            >
                                {confirmText}
                            </Button>
                        </div>

                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 p-2 text-white/20 hover:text-white transition-colors"
                        >
                            <X size={20} />
                        </button>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}
